import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { usePagination, SearchBar } from '../components/Pagination';
import { exportCSV } from '../utils/export.helpers';

const ETAT_COLOR = { Couverte: 'green', Planifiée: 'gold', Annulée: 'red', Reportée: 'navy' };

export default function HoraireCours() {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filters, setFilters]   = useState({ afficherPar: 'Classe', selectedId: '', from: new Date().toISOString().slice(0, 10), to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    api.get('/catalog/classes').then((c) => { setClasses(c || []); if (c?.[0]) setFilters((f) => ({ ...f, selectedId: c[0].id })); }).catch(() => {});
    api.get('/catalog/teachers').then(setTeachers).catch(() => {});
  }, []);

  function load() {
    setLoading(true);
    const param = filters.afficherPar === 'Classe' ? 'classId' : 'teacherId';
    api.get(`/timetable?${param}=${filters.selectedId}`).then((data) => {
      // Enrichir avec état simulé
      setSessions((data || []).map((s, i) => ({
        ...s,
        etat: i === 0 ? 'Couverte' : 'Planifiée',
        semaine: `Semaine ${Math.floor(i / 5) + 36}`,
      })));
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => { if (filters.selectedId) load(); }, [filters.selectedId, filters.afficherPar]);

  const list = filters.afficherPar === 'Classe' ? classes : teachers;
  const dimLabel = (x) => filters.afficherPar === 'Classe' ? x.name : `${x.lastName} ${x.firstName}`;

  const totalSeances   = sessions.length;
  const heuresCouvertes = sessions.filter((s) => s.etat === 'Couverte').reduce((sum, s) => sum + (s.startTime && s.endTime ? (parseInt(s.endTime) - parseInt(s.startTime)) / 100 : 1), 0);

  function doExport() {
    exportCSV(sessions.map((s) => ({
      Date: s.startTime,
      Heure: `${s.startTime} – ${s.endTime}`,
      Classe: s.class?.name || '—',
      Cours: s.subject?.name || '—',
      Professeur: s.teacher ? `${s.teacher.lastName} ${s.teacher.firstName}` : '—',
      Salle: s.room?.name || '—',
      État: s.etat,
    })), 'horaire_cours');
  }

  const DAYS_FR = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];


  const { paginated: seancesPaginated, search: hSearch, setSearch: setHSearch, Pagination: HPagination, total: hTotal, filtered: hFiltered } = usePagination(sessions, { pageSize: 5, searchKeys: ['etat', 'startTime', 'endTime'] });
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Horaire des cours</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            {totalSeances} séance(s) · {heuresCouvertes}h planifiée(s) dans la période
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm" onClick={() => window.print()}>🖨 Imprimer</button>
          <button className="btn-ghost btn-sm" onClick={doExport}>↓ Exporter</button>
          <button className="btn btn-sm">📅 Planifier</button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr auto', gap: 10, marginBottom: 16, alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0 }}><label>Afficher par</label>
          <select value={filters.afficherPar} onChange={(e) => setFilters({ ...filters, afficherPar: e.target.value, selectedId: '' })}>
            <option>Classe</option>
            <option>Professeur</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>{filters.afficherPar}</label>
          <select value={filters.selectedId} onChange={(e) => setFilters({ ...filters, selectedId: e.target.value })}>
            <option value="">Tous</option>
            {list.map((x) => <option key={x.id} value={x.id}>{dimLabel(x)}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Du</label><input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} /></div>
        <div className="field" style={{ margin: 0 }}><label>Au</label><input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} /></div>
        <button className="btn btn-sm" onClick={load} style={{ height: 38 }}>Afficher</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <SearchBar value={hSearch} onChange={setHSearch} placeholder="Classe, matière, état…" total={hTotal} filtered={hFiltered.length} />
          {loading ? <div className="loading">Chargement…</div> : hFiltered.length === 0 ? (
            <div className="empty">Aucune séance planifiée.</div>
          ) : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Date</th><th>Heure</th><th>Classe</th>
                  <th>Cours</th><th>Professeur</th><th>État du cours</th><th></th>
                </tr>
              </thead>
              <tbody>
                {seancesPaginated.map((s, i) => {
                  const dayLabel = s.dayOfWeek ? DAYS_FR[s.dayOfWeek] : '—';
                  const { green: _, ...rest } = ETAT_COLOR;
                  const etatBadge = ETAT_COLOR[s.etat] || 'gray';
                  return (
                    <tr key={s.id || i}>
                      <td>
                        <div style={{ fontWeight: 500 }}>—</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{dayLabel} · {s.semaine}</div>
                      </td>
                      <td className="mono">{s.startTime} – {s.endTime}<br /><span style={{ fontSize: 11, color: 'var(--muted)' }}>1 h</span></td>
                      <td><b>{s.class?.name || '—'}</b></td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.subject?.name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.subject?.code || ''}</div>
                      </td>
                      <td>{s.teacher ? `${s.teacher.lastName} ${s.teacher.firstName}` : '—'}</td>
                      <td><span className={`badge ${etatBadge}`}>{s.etat}</span></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {s.etat !== 'Couverte' && (
                          <>
                            <button className="btn-ghost btn-sm">📝</button>
                            <button className="btn-ghost btn-sm">✏</button>
                            <button className="link-danger">🗑</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </>
  );
}
